import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material';
import {
  lockdownTestCreate,
  lockdownTestDecrypt,
  lockdownTestRotate,
  lockdownTestRevoke,
} from '../../endpoints/lockdownTest';

const LockdownVaultTest: React.FC = () => {
  const [plaintext, setPlaintext] = useState('');
  const [token, setToken] = useState('');
  const [rotateText, setRotateText] = useState('');
  const [decrypted, setDecrypted] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setMessage(null);
    setBusy(true);
    try {
      await fn();
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        err.response?.data?.message ||
          err.message ||
          'Request failed (is hub lockdown test enabled?)'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Lockdown vault (test)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Calls peo-hub-go <code>/api/lockdown-test/*</code> with your session
          JWT; hub forwards to peo-lockdown. Requires hub env{' '}
          <code>PEO_HUB_LOCKDOWN_ENABLE_TEST_ROUTES</code> and lockdown running.
        </Typography>
      </Grid>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {message && (
        <Grid item xs={12}>
          <Alert severity="success">{message}</Alert>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Create
          </Typography>
          <TextField
            label="Plaintext"
            value={plaintext}
            onChange={(e) => setPlaintext(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            disabled={busy || !plaintext.trim()}
            onClick={() =>
              run(async () => {
                const res = await lockdownTestCreate(plaintext);
                setToken(res.token);
                setMessage(`Created token: ${res.token}`);
              })
            }
          >
            Create secret
          </Button>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Token
          </Typography>
          <TextField
            label="Vault token (UUID)"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Decrypt (get)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              disabled={busy || !token.trim()}
              onClick={() =>
                run(async () => {
                  const res = await lockdownTestDecrypt(token.trim());
                  setDecrypted(res.plaintext);
                  setMessage(`request_id: ${res.request_id}`);
                })
              }
            >
              Decrypt
            </Button>
            {decrypted !== null && (
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                Plaintext: {decrypted}
              </Typography>
            )}
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Rotate (update)
          </Typography>
          <TextField
            label="New plaintext"
            value={rotateText}
            onChange={(e) => setRotateText(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Button
            variant="outlined"
            disabled={busy || !token.trim() || !rotateText.trim()}
            onClick={() =>
              run(async () => {
                const res = await lockdownTestRotate(token.trim(), rotateText);
                setMessage(`Rotated ok; request_id: ${res.request_id}`);
              })
            }
          >
            Rotate
          </Button>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Revoke (delete)
          </Typography>
          <Button
            color="error"
            variant="outlined"
            disabled={busy || !token.trim()}
            onClick={() =>
              run(async () => {
                await lockdownTestRevoke(token.trim());
                setDecrypted(null);
                setMessage('Revoked (204 from hub/lockdown)');
              })
            }
          >
            Revoke token
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default LockdownVaultTest;
